import { ApiClient, groupBy } from '@msdk/common'
import {
  AllEntitiesMetadataSource,
  BaseMetadataSource,
  DesignModel,
  Entity,
  EntityMetadata,
  EntityMetadataSource,
  EntityModel,
  FieldDefinition,
  FieldMetadataSource,
  FormFieldDefinition,
  FormGroupDefinition,
  FormGroupMetadataSource,
  FormModel,
  FormTabDefinition,
  readBaseMetadataByName,
  readEntityModelByName,
  StringValue,
  syncEntityModel,
  UIElementDefinition,
} from '@msdk/definitions'

import { NodeGroup, NodeItem } from './node'

interface AllEntitiesResponse {
  name: string
  description: string
  tDescription: string
  idml: string
  microservice: string
}

export class SourceHandler {
  private readonly t: (idml: string, description: string) => string //cg using translation in a class can cause throuble with idTagging
  private readonly apiClient: ApiClient
  private readonly entity: Entity

  constructor(t: (idml: string, description: string) => string, apiClient: ApiClient, entity: Entity) {
    this.t = t
    this.apiClient = apiClient
    this.entity = entity
  }

  public async processSources(sources: BaseMetadataSource[]): Promise<NodeItem[]> {
    const data: NodeItem[] = []
    for (let i = 0; i < sources.length; i++) {
      const s = sources[i]
      const res = await this.getMedata(s)
      data.push(res)
    }

    const grouped = groupBy(data, 'entityName')
    const res: NodeItem[] = []

    const groupKeys = Object.keys(grouped)
    groupKeys.forEach((k) => {
      const items = grouped[k]
      //special case for entity source, there is already a node
      //for the entity containing all the fields, in this case
      //there should be only one item in the group, the node itself
      if (items.length == 1 && items[0].value == k) {
        res.push(items[0])
        return
      }

      const entityModel = readEntityModelByName(k) as EntityModel
      const baseMetadata = readBaseMetadataByName(entityModel.name) as EntityMetadata
      const tName = this.t(entityModel.idml, entityModel.description)
      const entityNode = new NodeGroup(
        k,
        entityModel.description,
        k,
        0,
        entityModel.idml,
        baseMetadata.msProvider,
        tName,
      )
      entityNode.children = items
      res.push(entityNode)
    })

    return res
  }

  public async getMedata(source: BaseMetadataSource): Promise<NodeItem> {
    let res: NodeItem
    switch (source.type) {
      case 'FieldSource':
        res = await this.metadataFromFieldSource(source as FieldMetadataSource)
        break
      case 'FormSource':
        res = await this.metadataFromFormSource(source as FormGroupMetadataSource)
        break
      case 'EntitySource':
        res = await this.metadataFromEntitySource(source as EntityMetadataSource)
        break
      case 'AllEntitiesSource':
        res = await this.metadataFromAllEntitySource(source as AllEntitiesMetadataSource)
        break
      default:
        throw new Error(`Unsupported metadata source: ${source.type}`)
    }
    return res
  }

  private async metadataFromFieldSource(source: FieldMetadataSource): Promise<NodeItem> {
    const entityName = source.entity
    const baseMetadata = readBaseMetadataByName(entityName) as EntityMetadata
    await this.ensureEntityMetadata(entityName, baseMetadata.msProvider)
    const model = readEntityModelByName(source.entity) as EntityModel
    const fDef = model.getFieldDefinition(source.name) as FieldDefinition
    const translation = this.translateField(fDef, source.includeTypeInDescription) //cg translation here can cause throuble with idTagging
    return new NodeItem(fDef.name, fDef.description, source.entity, 1, fDef.idml, baseMetadata.msProvider, translation)
  }

  private async metadataFromFormSource(source: FormGroupMetadataSource): Promise<NodeItem> {
    const entityName = source.entity
    const baseMetadata = readBaseMetadataByName(entityName) as EntityMetadata
    await this.ensureEntityMetadata(entityName, baseMetadata.msProvider)
    const model = readEntityModelByName(source.entity) as EntityModel
    const formModel = model.getForm(source.form) as FormModel
    const formGroup = this.getFormGroup(formModel.layout, source.name)
    if (!formGroup) throw new Error('[MetadataSelectorPanelComponent] form group not found!')

    const groupName = this.t(formGroup.idml, formGroup.description)
    const group = new NodeGroup(
      formGroup.name,
      formGroup.description,
      source.entity,
      1,
      formGroup.idml,
      baseMetadata.msProvider,
      groupName,
    )
    this.buildTree(formGroup.layout, group, model, source.includeTypeInDescription)
    group.sort()
    return group
  }

  private async metadataFromEntitySource(source: EntityMetadataSource): Promise<NodeItem> {
    const entityName = source.name
    const baseMetadata = readBaseMetadataByName(entityName) as EntityMetadata
    await this.ensureEntityMetadata(entityName, baseMetadata.msProvider)
    const model = readEntityModelByName(source.name) as EntityModel
    const entityNameTranslated = this.t(model.idml, model.description)
    const res = new NodeGroup(
      model.name,
      model.description,
      model.name,
      1,
      model.idml,
      baseMetadata.msProvider,
      entityNameTranslated,
    )
    const fieldsToExclude = source.excludeFields ? source.excludeFields.split(',') : []
    model.fields.forEach((f) => {
      if (!fieldsToExclude.some((x) => x === f.name)) {
        const translated = this.translateField(f, source.includeTypeInDescription)
        res.children.push(
          new NodeItem(f.name, f.description, model.name, 2, f.idml, baseMetadata.msProvider, translated),
        )
      }
    })
    res.sort()
    return res
  }

  private async metadataFromAllEntitySource(source: AllEntitiesMetadataSource): Promise<NodeItem> {
    const res = new NodeGroup(
      'ENTITY',
      'All entities',
      'ENTITY',
      1,
      'LBL_ALL_ENTITIES_METADATA_SELECTOR',
      'system',
      'All entities',
    )
    const tenant = source.fieldTenant ? (this.entity.fields[source.fieldTenant] as StringValue) : null
    const entitiesToExclude = source.excludeEntity ? source.excludeEntity.split(',') : []
    const entities = (await this.apiClient.read<AllEntitiesResponse[]>(
      `CMPS/api/composer/GetEntitiesForTenant${tenant ? `/${tenant.value}` : ''}?behaviourType=${
        source.behaviourType ?? 0
      }`,
    )) as AllEntitiesResponse[]
    entities.forEach((e) => {
      if (!entitiesToExclude.some((x) => x === e.name)) {
        res.children.push(
          new NodeItem(e.name, e.description, e.name, 2, e.idml, e.microservice, e.tDescription || e.description),
        )
      }
    })
    res.sort()
    return res
  }

  private async ensureEntityMetadata(entityName: string, msProvider: string): Promise<void> {
    const loadedModel = readEntityModelByName(entityName)
    if (!loadedModel) {
      const model = await this.apiClient.metadata.getModel(msProvider)
      const modelInstance = new DesignModel()
      modelInstance.merge(model as DesignModel, msProvider)
      await syncEntityModel(model)
    }
  }

  private translateField(fDef: FieldDefinition, includeType: boolean): string {
    let res = this.t(fDef.idml, fDef.description)
    if (includeType) res += ` (${fDef.fieldDefinitionType})`
    return res
  }

  private getFormGroup(layout: UIElementDefinition[], toFind: string): FormGroupDefinition {
    let res = null
    for (let i = 0; i < layout.length; i++) {
      const el = layout[i]
      if (el.type === 'Group') {
        const casted = el as FormGroupDefinition
        if (casted.name === toFind) {
          res = el
          break
        }

        res = this.getFormGroup(casted.layout, toFind)
      }
      if (el.type === 'Tab') {
        const casted = el as FormTabDefinition
        if (casted.name === toFind) {
          res = el
          break
        }

        res = this.getFormGroup(casted.layout, toFind)
      }

      if (res) break
    }

    return res as FormGroupDefinition
  }

  private buildTree(
    layout: UIElementDefinition[],
    container: NodeGroup,
    entityModel: EntityModel,
    includeType: boolean,
  ): void {
    const baseMetadata = readBaseMetadataByName(entityModel.name) as EntityMetadata
    for (let i = 0; i < layout.length; i++) {
      const el = layout[i]
      if (el.type === 'Field') {
        const casted = el as FormFieldDefinition
        const fDef = entityModel.getFieldDefinition(casted.name)
        const translated = this.translateField(fDef, includeType)
        const node = new NodeItem(
          fDef.name,
          fDef.description,
          entityModel.name,
          container.level + 1,
          fDef.idml,
          baseMetadata.msProvider,
          translated,
        )
        container.children.push(node)
      } else if (el.type === 'Group' || el.type === 'Tab') {
        const casted = el as FormGroupDefinition
        const groupName = this.t(casted.idml, casted.description)
        const group = new NodeGroup(
          casted.name,
          casted.description,
          entityModel.name,
          container.level + 1,
          casted.idml,
          baseMetadata.msProvider,
          groupName,
        )
        container.children.push(group)
        this.buildTree(casted.layout, group, entityModel, includeType)
      }
    }
  }
}
